import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, BarChart3, ListChecks, Wallet, CheckCircle, XCircle, Plus, Download } from "lucide-react";
import { toast } from "sonner";

const statusStyles = {
  approved: "bg-green-500/10 text-green-400 border-green-500/20",
  pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  rejected: "bg-red-500/10 text-red-400 border-red-500/20",
  processing: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  completed: "bg-green-500/10 text-green-400 border-green-500/20",
};

export default function AdminPage() {
  const [analytics, setAnalytics] = useState(null);
  const [ambassadors, setAmbassadors] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [waitlistSignups, setWaitlistSignups] = useState([]);
  const [newTask, setNewTask] = useState({ title: "", description: "", points: 50, type: "social_share", deadline: "" });
  const [taskDialog, setTaskDialog] = useState(false);
  const [newPayout, setNewPayout] = useState({ user_id: "", amount: 0, method: "Bank Transfer" });
  const [payoutDialog, setPayoutDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [a, amb, sub, pay, t, ws] = await Promise.all([
        api.get("/admin/analytics"), api.get("/admin/ambassadors"),
        api.get("/admin/submissions"), api.get("/admin/payouts"), api.get("/tasks"),
        api.get("/admin/waitlist-signups"),
      ]);
      setAnalytics(a.data); setAmbassadors(amb.data); setSubmissions(sub.data);
      setPayouts(pay.data); setTasks(t.data); setWaitlistSignups(ws.data);
    } catch (err) { console.error(err); }
  };

  const updateAmbStatus = async (id, status, reason = "") => {
    try { await api.put(`/admin/ambassadors/${id}/status`, { status, reason }); toast.success(`Ambassador ${status}`); setRejectDialog(null); setRejectReason(""); fetchAll(); }
    catch { toast.error("Failed to update"); }
  };
  const updateSubStatus = async (id, status) => {
    try { await api.put(`/admin/submissions/${id}/status`, { status }); toast.success(`Submission ${status}`); fetchAll(); }
    catch { toast.error("Failed to update"); }
  };
  const createTask = async () => {
    try { await api.post("/admin/tasks", newTask); toast.success("Task created"); setTaskDialog(false); setNewTask({ title: "", description: "", points: 50, type: "social_share", deadline: "" }); fetchAll(); }
    catch { toast.error("Failed to create task"); }
  };
  const deleteTask = async (id) => {
    try { await api.delete(`/admin/tasks/${id}`); toast.success("Task deleted"); fetchAll(); }
    catch { toast.error("Failed to delete"); }
  };
  const createPayout = async () => {
    try { await api.post("/admin/payouts", newPayout); toast.success("Payout created"); setPayoutDialog(false); setNewPayout({ user_id: "", amount: 0, method: "Bank Transfer" }); fetchAll(); }
    catch { toast.error("Failed to create payout"); }
  };
  const updatePayoutStatus = async (id, status) => {
    try { await api.put(`/admin/payouts/${id}/status`, { status }); toast.success(`Payout ${status}`); fetchAll(); }
    catch { toast.error("Failed to update"); }
  };

  const cards = analytics ? [
    { label: "Total Ambassadors", value: analytics.total_ambassadors, color: "text-blue-400" },
    { label: "Pending Applications", value: analytics.pending_applications, color: "text-[#F7941D]" },
    { label: "Total Clicks", value: analytics.total_clicks, color: "text-cyan-400" },
    { label: "Total Signups", value: analytics.total_signups, color: "text-green-400" },
    { label: "Total Earnings", value: `$${(analytics.total_earnings || 0).toFixed(2)}`, color: "text-emerald-400" },
    { label: "Active Tasks", value: analytics.active_tasks, color: "text-purple-400" },
    { label: "Pending Reviews", value: analytics.pending_submissions, color: "text-rose-400" },
    { label: "Pending Payouts", value: analytics.pending_payouts || 0, color: "text-yellow-400" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="animate-fade-in-up">
        <h1 className="text-2xl sm:text-3xl font-semibold text-white/90 tracking-tight">Admin Panel</h1>
        <p className="text-white/35 text-sm mt-1">Manage ambassadors, tasks, and payouts</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-white/[0.04] rounded-xl border border-white/[0.06] flex-wrap">
          <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="admin-tab-overview"><BarChart3 className="w-3.5 h-3.5 mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="ambassadors" className="rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="admin-tab-ambassadors"><Users className="w-3.5 h-3.5 mr-1.5" />Ambassadors</TabsTrigger>
          <TabsTrigger value="tasks" className="rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="admin-tab-tasks"><ListChecks className="w-3.5 h-3.5 mr-1.5" />Tasks</TabsTrigger>
          <TabsTrigger value="submissions" className="rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="admin-tab-submissions">Submissions</TabsTrigger>
          <TabsTrigger value="payouts" className="rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="admin-tab-payouts"><Wallet className="w-3.5 h-3.5 mr-1.5" />Payouts</TabsTrigger>
          <TabsTrigger value="waitlist" className="rounded-lg data-[state=active]:bg-white/[0.08] data-[state=active]:text-white/90 text-white/40" data-testid="admin-tab-waitlist">Waitlist</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
            {cards.map(({ label, value, color }) => (
              <div key={label} className="glass glass-hover rounded-2xl p-5 animate-fade-in-up">
                <p className={`font-mono text-2xl font-semibold ${color}`}>{value}</p>
                <p className="text-xs text-white/30 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="ambassadors">
          <div className="glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Name</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Email</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Code</th>
                    <th className="text-right text-white/35 text-xs font-medium py-3 px-5">Points</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Status</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ambassadors.map((a) => (
                    <tr key={a._id} className="border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02] transition-colors" data-testid={`ambassador-row-${a._id}`}>
                      <td className="text-sm font-medium text-white/75 py-3 px-5">{a.name}</td>
                      <td className="text-xs text-white/40 font-mono py-3 px-5">{a.email}</td>
                      <td className="font-mono text-xs text-[#F7941D] py-3 px-5">{a.affiliate_code}</td>
                      <td className="font-mono text-sm text-right text-white/50 py-3 px-5">{a.points}</td>
                      <td className="py-3 px-5"><Badge className={statusStyles[a.status] || ""} variant="outline">{a.status}</Badge></td>
                      <td className="py-3 px-5">
                        <div className="flex gap-1">
                          {a.status !== "approved" && (
                            <button className="h-7 px-2 rounded-lg text-green-400 hover:bg-green-500/10 transition-colors" onClick={() => updateAmbStatus(a._id, "approved")} data-testid={`approve-${a._id}`}>
                              <CheckCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {a.status !== "rejected" && (
                            <Dialog open={rejectDialog === a._id} onOpenChange={(open) => { setRejectDialog(open ? a._id : null); if (!open) setRejectReason(""); }}>
                              <DialogTrigger asChild>
                                <button className="h-7 px-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`reject-${a._id}`}>
                                  <XCircle className="w-3.5 h-3.5" />
                                </button>
                              </DialogTrigger>
                              <DialogContent className="glass-strong rounded-2xl border-white/[0.08]">
                                <DialogHeader><DialogTitle className="text-white/90">Reject {a.name}'s Application</DialogTitle></DialogHeader>
                                <div className="space-y-4 mt-2">
                                  <div className="space-y-2">
                                    <Label className="text-white/60 text-sm font-medium">Reason for rejection</Label>
                                    <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="e.g., Incomplete profile" className="glass-input rounded-xl h-11 text-sm" data-testid="reject-reason-input" />
                                  </div>
                                  <button className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold rounded-full h-11 text-sm transition-all duration-300" onClick={() => updateAmbStatus(a._id, "rejected", rejectReason)} data-testid="confirm-reject-button">
                                    Reject Application
                                  </button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tasks">
          <div className="flex justify-end mb-4">
            <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
              <DialogTrigger asChild>
                <button className="bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full px-4 py-2 text-sm transition-all duration-300 flex items-center gap-1.5" data-testid="create-task-button">
                  <Plus className="w-4 h-4" />New Task
                </button>
              </DialogTrigger>
              <DialogContent className="glass-strong rounded-2xl border-white/[0.08]">
                <DialogHeader><DialogTitle className="text-white/90">Create Task</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm font-medium">Title</Label>
                    <Input value={newTask.title} onChange={e => setNewTask({ ...newTask, title: e.target.value })} className="glass-input rounded-xl h-11 text-sm" data-testid="task-title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm font-medium">Description</Label>
                    <Input value={newTask.description} onChange={e => setNewTask({ ...newTask, description: e.target.value })} className="glass-input rounded-xl h-11 text-sm" data-testid="task-desc-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/60 text-sm font-medium">Points</Label>
                      <Input type="number" value={newTask.points} onChange={e => setNewTask({ ...newTask, points: parseInt(e.target.value) || 0 })} className="glass-input rounded-xl h-11 text-sm" data-testid="task-points-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/60 text-sm font-medium">Type</Label>
                      <Select value={newTask.type} onValueChange={v => setNewTask({ ...newTask, type: v })}>
                        <SelectTrigger className="glass-input rounded-xl h-11 text-sm" data-testid="task-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="glass-strong rounded-xl border-white/[0.08]">
                          <SelectItem value="social_share">Social Share</SelectItem>
                          <SelectItem value="content">Content</SelectItem>
                          <SelectItem value="referral">Referral</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <button className="w-full bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full h-11 text-sm transition-all duration-300" onClick={createTask} data-testid="save-task-button">Create Task</button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Title</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Type</th>
                    <th className="text-right text-white/35 text-xs font-medium py-3 px-5">Points</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t) => (
                    <tr key={t.id} className="border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02] transition-colors">
                      <td className="text-sm text-white/70 py-3 px-5">{t.title}</td>
                      <td className="py-3 px-5"><Badge variant="outline" className="text-[10px] border-white/[0.08] uppercase tracking-wider bg-white/[0.04] text-white/40">{t.type?.replace("_", " ")}</Badge></td>
                      <td className="font-mono text-sm text-right text-[#F7941D] font-medium py-3 px-5">{t.points}</td>
                      <td className="py-3 px-5">
                        <button className="h-7 px-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors" onClick={() => deleteTask(t.id)} data-testid={`delete-task-${t.id}`}>
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="submissions">
          <div className="glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Task</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Ambassador</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Proof</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Status</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s) => (
                    <tr key={s.id} className="border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02] transition-colors" data-testid={`submission-row-${s.id}`}>
                      <td className="text-sm text-white/70 py-3 px-5">{s.task_title}</td>
                      <td className="text-sm text-white/50 py-3 px-5">{s.user_name}</td>
                      <td className="py-3 px-5">
                        <a href={s.proof_url} target="_blank" rel="noopener noreferrer" className="text-[#F7941D] text-xs underline hover:text-[#ff9f2e]">View</a>
                      </td>
                      <td className="py-3 px-5"><Badge className={statusStyles[s.status] || ""} variant="outline">{s.status}</Badge></td>
                      <td className="py-3 px-5">
                        {s.status === "pending" && (
                          <div className="flex gap-1">
                            <button className="h-7 px-2 rounded-lg text-green-400 hover:bg-green-500/10 transition-colors" onClick={() => updateSubStatus(s.id, "approved")} data-testid={`approve-sub-${s.id}`}>
                              <CheckCircle className="w-3.5 h-3.5" />
                            </button>
                            <button className="h-7 px-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors" onClick={() => updateSubStatus(s.id, "rejected")} data-testid={`reject-sub-${s.id}`}>
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {submissions.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-white/25 py-8">No submissions yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payouts">
          <div className="flex justify-end mb-4">
            <Dialog open={payoutDialog} onOpenChange={setPayoutDialog}>
              <DialogTrigger asChild>
                <button className="bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full px-4 py-2 text-sm transition-all duration-300 flex items-center gap-1.5" data-testid="create-payout-button">
                  <Plus className="w-4 h-4" />New Payout
                </button>
              </DialogTrigger>
              <DialogContent className="glass-strong rounded-2xl border-white/[0.08]">
                <DialogHeader><DialogTitle className="text-white/90">Create Payout</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm font-medium">Ambassador</Label>
                    <Select value={newPayout.user_id} onValueChange={v => setNewPayout({ ...newPayout, user_id: v })}>
                      <SelectTrigger className="glass-input rounded-xl h-11 text-sm" data-testid="payout-user-select"><SelectValue placeholder="Select ambassador" /></SelectTrigger>
                      <SelectContent className="glass-strong rounded-xl border-white/[0.08]">
                        {ambassadors.filter(a => a.status === "approved").map(a => (
                          <SelectItem key={a._id} value={a._id}>{a.name} ({a.email})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm font-medium">Amount ($)</Label>
                    <Input type="number" value={newPayout.amount} onChange={e => setNewPayout({ ...newPayout, amount: parseFloat(e.target.value) || 0 })} className="glass-input rounded-xl h-11 text-sm" data-testid="payout-amount-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm font-medium">Method</Label>
                    <Select value={newPayout.method} onValueChange={v => setNewPayout({ ...newPayout, method: v })}>
                      <SelectTrigger className="glass-input rounded-xl h-11 text-sm" data-testid="payout-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="glass-strong rounded-xl border-white/[0.08]">
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="E-Transfer">E-Transfer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <button className="w-full bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full h-11 text-sm transition-all duration-300" onClick={createPayout} data-testid="save-payout-button">Create Payout</button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Date</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Ambassador</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Method</th>
                    <th className="text-right text-white/35 text-xs font-medium py-3 px-5">Amount</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Status</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id} className="border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02] transition-colors" data-testid={`payout-row-${p.id}`}>
                      <td className="font-mono text-xs text-white/35 py-3 px-5">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="text-sm text-white/60 py-3 px-5">{p.user_name || "---"}</td>
                      <td className="text-sm text-white/50 py-3 px-5">{p.method}</td>
                      <td className="font-mono text-sm text-right text-green-400 font-medium py-3 px-5">${(p.amount || 0).toFixed(2)}</td>
                      <td className="py-3 px-5"><Badge className={statusStyles[p.status] || ""} variant="outline">{p.status}</Badge></td>
                      <td className="py-3 px-5">
                        {p.status === "pending" && (
                          <div className="flex gap-1">
                            <button className="h-7 px-2 rounded-lg text-blue-400 hover:bg-blue-500/10 text-xs transition-colors" onClick={() => updatePayoutStatus(p.id, "processing")} data-testid={`process-payout-${p.id}`}>Process</button>
                            <button className="h-7 px-2 rounded-lg text-green-400 hover:bg-green-500/10 text-xs transition-colors" onClick={() => updatePayoutStatus(p.id, "completed")} data-testid={`complete-payout-${p.id}`}>Complete</button>
                          </div>
                        )}
                        {p.status === "processing" && (
                          <button className="h-7 px-2 rounded-lg text-green-400 hover:bg-green-500/10 text-xs transition-colors" onClick={() => updatePayoutStatus(p.id, "completed")}>Complete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {payouts.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-white/25 py-8">No payouts yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="waitlist">
          <div className="flex justify-end mb-4">
            <button className="glass glass-hover rounded-full px-4 py-2 text-sm text-white/50 hover:text-white/80 flex items-center gap-1.5 transition-all duration-300" onClick={async () => {
              try {
                const response = await api.get("/admin/waitlist-signups/export", { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'eamico_waitlist_signups.csv');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success("CSV exported!");
              } catch { toast.error("Export failed"); }
            }} data-testid="export-csv-button">
              <Download className="w-4 h-4" />Export CSV
            </button>
          </div>
          <div className="glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Name</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Email</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Phone</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 px-5">Referred By</th>
                    <th className="text-right text-white/35 text-xs font-medium py-3 px-5">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {waitlistSignups.map((s) => (
                    <tr key={s.id} className="border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02] transition-colors">
                      <td className="text-sm text-white/70 font-medium py-3 px-5">{s.first_name} {s.last_name}</td>
                      <td className="text-xs text-white/40 font-mono py-3 px-5">{s.email}</td>
                      <td className="text-xs text-white/35 py-3 px-5">{s.phone}</td>
                      <td className="text-xs text-[#F7941D] py-3 px-5">{s.ambassador_name}</td>
                      <td className="text-xs text-white/25 text-right font-mono py-3 px-5">{new Date(s.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {waitlistSignups.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-white/25 py-8">No waitlist signups yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
